<template>
  <AppModal :visible="visible"
            :title="$t('general.select_aspect_ratio')"
            width="760px"
            max-height="90vh"
            :show-close-button="!applying"
            :close-on-overlay="!applying"
            @update:visible="handleClose"
  >
    <template #header>
      <h2 class="app-modal-title">
        <i class="uil uil-crop-alt"></i>
        {{ $t("general.select_aspect_ratio") }}
      </h2>
    </template>
    <div>
        <div class="image-ratio-preview-wrap">
          <div ref="imageRatioPreview"
               :style="{ aspectRatio: getImageAspectRatio(selectedImageAspect) }"
               class="image-ratio-preview"
               :data-aspect-ratio="selectedImageAspect"
               @mousedown.prevent="startImageDrag"
               @touchstart.prevent="startImageDrag"
          >
            <img v-if="imageEditPreviewUrl"
                 :src="imageEditPreviewUrl"
                 :style="imagePreviewStyle"
                 alt="preview"
                 class="image-ratio-preview-image"
            />
          </div>
        </div>

        <div class="image-ratio-options">
          <button v-for="option in imageAspectOptions"
                  :key="option.id"
                  :class="{ 'image-ratio-option-btn--active btn-primary': selectedImageAspect === option.id }"
                  :disabled="applying"
                  class="btn image-ratio-option-btn"
                  type="button"
                  @click="selectedImageAspect = option.id"
          >
            {{ option.name }}
          </button>
        </div>
      </div>
      <template #footer>
        <button :disabled="applying"
                class="btn btn-outline-secondary"
                type="button"
                @click="handleUseOriginal"
        >
          {{ $t('general.use_original') }}
        </button>
        <button :disabled="applying"
                class="btn btn-primary"
                type="button"
                @click="handleApply"
        >
          {{ $t('general.apply') }}
        </button>
      </template>
  </AppModal>
</template>

<script>
import AppModal from "@/view/components/ui/AppModal.vue";
import { IMAGE_ASPECT_RATIOS } from "@/utils/imageCrop";

const IMAGE_ASPECT_OPTIONS = Object.freeze(
  Object.keys(IMAGE_ASPECT_RATIOS).map((aspectRatio) =>
    Object.freeze({ id: aspectRatio, name: aspectRatio })
  )
);

export default {
  name: "ImageRatioModal",
  components: {
    AppModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    imageEditPreviewUrl: {
      type: String,
      default: null,
    },
    imageNaturalWidth: {
      type: Number,
      default: 0,
    },
    imageNaturalHeight: {
      type: Number,
      default: 0,
    },
    applying: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedImageAspect: "1:1",
      imageCropAnchorX: 0.5,
      imageCropAnchorY: 0.5,
      imageDragging: false,
      imageDragStartX: 0,
      imageDragStartY: 0,
      imageDragStartAnchorX: 0.5,
      imageDragStartAnchorY: 0.5,
    };
  },
  computed: {
    imageAspectOptions() {
      return IMAGE_ASPECT_OPTIONS;
    },
    imagePreviewStyle() {
      let cursor = "grab";
      if (this.applying) cursor = "wait";
      else if (this.imageDragging) cursor = "grabbing";

      return {
        objectPosition: `${(this.imageCropAnchorX * 100).toFixed(2)}% ${(this.imageCropAnchorY * 100).toFixed(2)}%`,
        cursor,
      };
    },
  },
  methods: {
    handleClose() {
      if (this.applying) return;
      this.$emit('close');
    },
    handleUseOriginal() {
      if (this.applying) return;
      this.$emit('use-original');
    },
    handleApply() {
      if (this.applying) return;
      this.$emit('apply', {
        selectedAspect: this.selectedImageAspect,
        anchorX: this.imageCropAnchorX,
        anchorY: this.imageCropAnchorY,
      });
    },
    getImageAspectRatio(aspectKey) {
      return IMAGE_ASPECT_RATIOS[aspectKey]
        ? aspectKey.replace(":", " / ")
        : "1 / 1";
    },
    getImageDragPoint(event) {
      if (event?.touches?.length) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      if (event?.changedTouches?.length) {
        return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }
      return { x: event?.clientX || 0, y: event?.clientY || 0 };
    },
    getSelectedAspectRatioNumber() {
      return IMAGE_ASPECT_RATIOS[this.selectedImageAspect] || 1;
    },
    getPreviewDragMetrics() {
      const el = this.$refs.imageRatioPreview;
      const srcW = this.imageNaturalWidth;
      const srcH = this.imageNaturalHeight;
      const targetRatio = this.getSelectedAspectRatioNumber();

      if (!el || !srcW || !srcH || !targetRatio) {
        return null;
      }

      const rect = el.getBoundingClientRect();
      const frameW = rect.width || 0;
      const frameH = rect.height || 0;
      if (!frameW || !frameH) return null;

      const srcRatio = srcW / srcH;
      let displayW = frameW;
      let displayH = frameH;

      if (srcRatio > targetRatio) {
        displayH = frameH;
        displayW = frameH * srcRatio;
      } else {
        displayW = frameW;
        displayH = frameW / srcRatio;
      }

      return {
        overflowX: Math.max(0, displayW - frameW),
        overflowY: Math.max(0, displayH - frameH),
      };
    },
    startImageDrag(event) {
      if (this.applying) return;
      const metrics = this.getPreviewDragMetrics();
      if (!metrics || (!metrics.overflowX && !metrics.overflowY)) return;

      const point = this.getImageDragPoint(event);
      this.imageDragging = true;
      this.imageDragStartX = point.x;
      this.imageDragStartY = point.y;
      this.imageDragStartAnchorX = this.imageCropAnchorX;
      this.imageDragStartAnchorY = this.imageCropAnchorY;

      window.addEventListener("mousemove", this.onImageDrag, { passive: false });
      window.addEventListener("mouseup", this.stopImageDrag);
      window.addEventListener("touchmove", this.onImageDrag, { passive: false });
      window.addEventListener("touchend", this.stopImageDrag);
      window.addEventListener("touchcancel", this.stopImageDrag);
    },
    onImageDrag(event) {
      if (!this.imageDragging) return;
      const metrics = this.getPreviewDragMetrics();
      if (!metrics) return;

      const point = this.getImageDragPoint(event);
      const deltaX = point.x - this.imageDragStartX;
      const deltaY = point.y - this.imageDragStartY;

      let nextX = this.imageDragStartAnchorX;
      let nextY = this.imageDragStartAnchorY;

      if (metrics.overflowX > 0) {
        nextX = this.imageDragStartAnchorX - deltaX / metrics.overflowX;
      }
      if (metrics.overflowY > 0) {
        nextY = this.imageDragStartAnchorY - deltaY / metrics.overflowY;
      }

      this.imageCropAnchorX = Math.max(0, Math.min(1, nextX));
      this.imageCropAnchorY = Math.max(0, Math.min(1, nextY));

      if (event?.cancelable) {
        event.preventDefault();
      }
    },
    stopImageDrag() {
      this.imageDragging = false;
      window.removeEventListener("mousemove", this.onImageDrag);
      window.removeEventListener("mouseup", this.stopImageDrag);
      window.removeEventListener("touchmove", this.onImageDrag);
      window.removeEventListener("touchend", this.stopImageDrag);
      window.removeEventListener("touchcancel", this.stopImageDrag);
    },
    reset() {
      this.selectedImageAspect = "1:1";
      this.imageCropAnchorX = 0.5;
      this.imageCropAnchorY = 0.5;
      this.stopImageDrag();
    },
  },
  beforeDestroy() {
    this.stopImageDrag();
  },
};
</script>

<style scoped>
/* Modal Dialog */
.modal-image-ratio-dialog {
  max-width: 760px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.image-ratio-modal-close {
  position: absolute;
  z-index: 1;
  top: 10px;
  right: 10px;
  padding: 5px;
  border: 0;
  background: transparent;
  color: inherit;
}

.image-ratio-modal-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Preview Wrap */
.image-ratio-preview-wrap {
  background: #f3f5f8;
  border: 1px solid #e1e7ef;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 14px;
}

/* Preview Container */
.image-ratio-preview {
  width: 100%;
  height: auto;
  background: #0f172a;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  user-select: none;
  touch-action: none;
  margin: 0 auto;
}

.image-ratio-preview[data-aspect-ratio="1:1"] {
  max-width: min(100%, 420px, 60vh);
}

.image-ratio-preview[data-aspect-ratio="4:1"] {
  max-width: min(100%, 1680px, 240vh);
}

.image-ratio-preview[data-aspect-ratio="3:4"] {
  max-width: min(100%, 315px, 45vh);
}

.image-ratio-preview[data-aspect-ratio="16:9"] {
  max-width: min(100%, 746.67px, 106.67vh);
}

/* Preview Image */
.image-ratio-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Aspect Ratio Options */
.image-ratio-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.image-ratio-option-btn {
  border: 1px solid #d0d7e2;
  background: #ffffff;
  color: #374151;
  border-radius: 999px;
  min-height: 36px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.image-ratio-option-btn:hover {
  background: #f8f9fa;
  border-color: #b8c0cc;
}

.image-ratio-option-btn--active {
  color: #ffffff;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-image-ratio-dialog {
    max-width: calc(100vw - 20px);
    margin: 10px auto;
    border-radius: 14px;
  }

  .image-ratio-preview[data-aspect-ratio="1:1"] {
    max-width: min(100%, 280px, 48vh);
  }

  .image-ratio-preview[data-aspect-ratio="4:1"] {
    max-width: min(100%, 1120px, 192vh);
  }

  .image-ratio-preview[data-aspect-ratio="3:4"] {
    max-width: min(100%, 210px, 36vh);
  }

  .image-ratio-preview[data-aspect-ratio="16:9"] {
    max-width: min(100%, 497.78px, 85.33vh);
  }
}
</style>
